import { Rive } from "@rive-app/react-canvas";
import React from "react";
import { SelectList } from "web-ui";
import { LegacyButton as Button } from "web-ui/legacy";

import { getDuolingoTtsLink } from "../api/duolingoTts";
import type { PhonemeSpan } from "../api/transcription";
import {
  CharactersToRigsMapping,
  characters,
  defaultRiveRig,
} from "../rive/characters";
import styles from "../styles/VisemeContainer.module.scss";

import { VisemeAnimation } from "./VisemeAnimation";

const OPTION_DESIGNATOR = "or";
const SWITCH = "Switch to custom rig";
const RIG_PROMPT = "Use a custom viseme animation rig:";

const STATE_MACHINE_NAME = "InLesson";
const ARTBOARD_NAME = "Character";

const voiceToRig = (voice: string): string => {
  const character = Object.keys(CharactersToRigsMapping).find(characterName =>
    voice.startsWith(characterName),
  );
  return character ? CharactersToRigsMapping[character] : defaultRiveRig;
};

export const VisemeContainer = ({
  audioRef,
  phonemeSpans,
  ttsHash,
  voice,
}: {
  audioRef: React.MutableRefObject<HTMLAudioElement | undefined>;
  phonemeSpans: PhonemeSpan[];
  ttsHash: string;
  voice: string;
}) => {
  const [visemeRigSrcOrBuffer, setVisemeRigSrcOrBuffer] = React.useState<{
    src?: string;
    buffer?: ArrayBuffer;
  }>({ src: voiceToRig(voice) });
  const [visemeKey, setVisemeKey] = React.useState(0);
  const [stateMachineName, setStateMachineName] =
    React.useState(STATE_MACHINE_NAME);
  const [artboardName, setArtboardName] = React.useState(ARTBOARD_NAME);

  const [uploadedRigFile, setUploadedRigFile] = React.useState<ArrayBuffer>();
  const [uploadedStateMachineName, setUploadedStateMachineName] =
    React.useState<string>();
  const [uploadedArtboardName, setUploadedArtboardName] =
    React.useState<string>();
  const [availableArtboards, setAvailableArtboards] = React.useState<string[]>(
    [],
  );
  const [availableStateMachines, setAvailableStateMachines] = React.useState<
    string[]
  >([]);

  const uploadedArtboardIndex = availableArtboards.indexOf(
    uploadedArtboardName ?? "",
  );
  const uploadedStateMachineIndex = availableStateMachines.indexOf(
    uploadedStateMachineName ?? "",
  );

  React.useEffect(() => {
    setUploadedArtboardName(undefined);
    setUploadedStateMachineName(undefined);
    if (uploadedRigFile) {
      const canvas = document.createElement("canvas");
      // Creating Rive object for sole purpose of determining the names
      // of new artboards
      const rive = new Rive({
        buffer: uploadedRigFile,
        canvas,
        onLoad: () => {
          if (rive.contents?.artboards) {
            setAvailableArtboards(
              rive.contents.artboards.map(artboard => artboard.name),
            );
          }
        },
      });
    } else {
      setAvailableArtboards([]);
    }
  }, [uploadedRigFile]);

  React.useEffect(() => {
    setUploadedStateMachineName(undefined);
    if (uploadedRigFile && uploadedArtboardName) {
      const canvas = document.createElement("canvas");
      // Creating Rive object for sole purpose of determining the names
      // of new state machines
      const rive = new Rive({
        buffer: uploadedRigFile,
        canvas,
        onLoad: () => {
          if (rive.contents?.artboards) {
            const selectedArtboard = rive.contents.artboards.find(
              board => board.name === uploadedArtboardName,
            );
            if (selectedArtboard) {
              setAvailableStateMachines(
                selectedArtboard.stateMachines.map(machine => machine.name),
              );
            }
          }
        },
      });
    } else {
      setAvailableStateMachines([]);
    }
  }, [uploadedRigFile, uploadedArtboardName]);

  const switchToNewRig = (
    rigSrc: string | undefined,
    rigBuffer: ArrayBuffer | undefined,
    newArtboardName: string | undefined,
    newStateMachineName: string | undefined,
  ) => {
    if (newArtboardName && newStateMachineName) {
      // Prefer built in rigs over uploaded rigs if for some reason both are passed in
      const source = rigSrc
        ? { src: rigSrc }
        : rigBuffer
          ? { buffer: rigBuffer }
          : null;
      if (source) {
        setVisemeRigSrcOrBuffer(source);
        setStateMachineName(newStateMachineName);
        setArtboardName(newArtboardName);
        setVisemeKey(key => key + 1);
      }
    }
  };

  return (
    <div className={styles["viseme-container"]}>
      <div className={styles["viseme-animation"]}>
        <VisemeAnimation
          artboardName={artboardName}
          audioRef={audioRef}
          audioSource={getDuolingoTtsLink(voice, ttsHash)}
          key={visemeKey}
          phonemeSpans={phonemeSpans}
          stateMachineName={stateMachineName}
          visemeRigSrcOrBuffer={visemeRigSrcOrBuffer}
        />
      </div>
      <div className={styles["viseme-form"]}>
        <SelectList
          items={characters.map(key => ({
            text: key.charAt(0).toUpperCase() + key.slice(1),
          }))}
          onChange={e => {
            const characterName: string | undefined =
              characters[e.selectedIndices[0]];
            const src = characterName
              ? CharactersToRigsMapping[characterName]
              : defaultRiveRig;
            switchToNewRig(src, undefined, ARTBOARD_NAME, STATE_MACHINE_NAME);
          }}
          popoverPosition={{
            direction: "down",
            manualPositioning: true,
            style: {
              marginTop: 50,
              maxHeight: 426,
              position: "absolute",
            },
            zIndex: 51,
          }}
          selectedIndices={[]}
          showSearch={true}
          text="Switch to a different built in rig"
        />
        <p className={styles["center"]}>{OPTION_DESIGNATOR}</p>
        <p>{RIG_PROMPT}</p>
        <input
          className={styles["viseme-switch-input"]}
          onChange={e => {
            const file = e.target.files?.[0];
            if (file) {
              const fileReader = new FileReader();
              fileReader.readAsArrayBuffer(file);
              fileReader.onload = () => {
                if (
                  fileReader.result &&
                  fileReader.result instanceof ArrayBuffer
                ) {
                  setUploadedRigFile(fileReader.result);
                }
              };
            }
          }}
          type="file"
        />
        <SelectList
          className={styles["viseme-switch-input"]}
          disabled={uploadedRigFile === undefined}
          items={availableArtboards.map(name => ({
            text: name,
          }))}
          onChange={e =>
            setUploadedArtboardName(
              availableArtboards[e.selectedIndices[0]] ?? "",
            )
          }
          popoverPosition={{
            direction: "down",
            manualPositioning: true,
            style: {
              marginTop: 50,
              maxHeight: 426,
              position: "absolute",
            },
            zIndex: 51,
          }}
          selectedIndices={
            uploadedArtboardIndex === -1 ? [] : [uploadedArtboardIndex]
          }
          showSearch={true}
          text={
            availableArtboards.length > 0
              ? uploadedArtboardName
                ? uploadedArtboardName
                : "Artboard"
              : "No artboards available"
          }
        />
        <SelectList
          className={styles["viseme-switch-input"]}
          disabled={uploadedRigFile === undefined}
          items={availableStateMachines.map(name => ({
            text: name,
          }))}
          onChange={e =>
            setUploadedStateMachineName(
              availableStateMachines[e.selectedIndices[0]] ?? "",
            )
          }
          popoverPosition={{
            direction: "down",
            manualPositioning: true,
            style: {
              marginTop: 50,
              maxHeight: 426,
              position: "absolute",
            },
            zIndex: 51,
          }}
          selectedIndices={
            uploadedStateMachineIndex === -1 ? [] : [uploadedStateMachineIndex]
          }
          showSearch={true}
          text={
            availableStateMachines.length > 0
              ? uploadedStateMachineName
                ? uploadedStateMachineName
                : "State Machine"
              : "No state machines available"
          }
        />
        <Button
          className={styles["viseme-switch-button"]}
          color="macaw"
          disabled={
            uploadedRigFile === undefined ||
            uploadedArtboardName === undefined ||
            uploadedStateMachineName === undefined
          }
          onClick={() => {
            switchToNewRig(
              undefined,
              uploadedRigFile,
              uploadedArtboardName,
              uploadedStateMachineName,
            );
          }}
          submitting={false}
          type="submit"
          variant="solid"
        >
          {SWITCH}
        </Button>
      </div>
    </div>
  );
};
